import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { StatusBadge } from '../../components';
import { apiService } from '../../services';
import type { PipelineStageState, ScoringRunDetail, StageOutcomeCount } from '../../types';
import { formatMonitorStamp } from '../../utils/monitorFormat';
import {
  runStatusLabel,
  runStatusTone,
  stageLabel,
  stageRowStateLabel,
  stageRowTone,
} from '../../utils/monitorStatus';
import { classificationMethod, classificationMethodLabel } from './classificationMethod';
import { outcomeLabel, visibleOutcomes } from './outcomeLabels';
import { stageClocks, type StepClock } from './stepTimes';
import '../monitoring/monitoring.css';

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);
const WaitIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden>
    <circle cx="12" cy="12" r="2" />
  </svg>
);

function cardClass(state: PipelineStageState): string {
  if (state === 'failed') return 'fail';
  if (state === 'done') return 'done';
  if (state === 'running') return 'running';
  if (state === 'pending') return 'idle';
  return '';
}

function partClass(state: PipelineStageState): string {
  const parts = ['stg-part'];
  if (state === 'failed') parts.push('bad');
  if (state === 'running') parts.push('running');
  if (state === 'done') parts.push('done');
  return parts.join(' ');
}

function Dot({ state }: { state: PipelineStageState }) {
  if (state === 'failed') {
    return <span className="stg-dot no"><XIcon /></span>;
  }
  if (state === 'done') {
    return <span className="stg-dot ok"><CheckIcon /></span>;
  }
  return <span className="stg-dot wait"><WaitIcon /></span>;
}

function progressPct(through: number | undefined, of: number): number {
  if (!of) return 0;
  return Math.max(0, Math.min(100, Math.round(((through || 0) / of) * 100)));
}

function StepTimes({ clock }: { clock: StepClock }) {
  return (
    <>
      <span className="sub">Started {formatMonitorStamp(clock.startedAt)}</span>
      <span className="sub">Finished {formatMonitorStamp(clock.finishedAt)}</span>
    </>
  );
}

function OutcomeLines({
  outcomes,
  className,
}: {
  outcomes: StageOutcomeCount[] | undefined;
  className: string;
}) {
  const rows = visibleOutcomes(outcomes);
  if (!rows.length) return null;
  return (
    <>
      {rows.map((row) => (
        <span key={row.code} className={className}>
          {outcomeLabel(row.code)} · {row.count}
        </span>
      ))}
    </>
  );
}

export const ScoringRun: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const [data, setData] = useState<ScoringRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getScoringRun(runId)
      .then((res) => {
        if (cancelled) return;
        if (res.success) setData(res.data);
        else setError(res.message || 'Scoring run not found');
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load scoring run');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  const progress = data?.stage_progress;
  const of = progress?.of ?? 0;
  const tone = runStatusTone(data?.status);
  const stopped = data?.status === 'failed'
    ? `stopped in the ${stageLabel(data.current_stage)} stage`
    : data?.status === 'completed'
      ? 'finished'
      : data
        ? `in ${stageLabel(data.current_stage)}`
        : '';

  const clocks = data ? stageClocks(data.steps ?? []) : null;
  const classifyingWith = data ? classificationMethodLabel(classificationMethod(data)) : null;
  const tableRows: Array<{
    label: string;
    extra?: string;
    state: PipelineStageState;
    through: number | undefined;
    clock: StepClock;
    outcomes?: StageOutcomeCount[];
  }> = progress && clocks
    ? [
        {
          label: 'Summarizing',
          state: progress.summarizing.state,
          through: progress.summarizing.through,
          clock: clocks.summarizing,
          outcomes: progress.summarizing.outcomes,
        },
        {
          label: 'Classifying',
          extra: classifyingWith ? ` · ${classifyingWith}` : undefined,
          state: progress.classifying.state,
          through: progress.classifying.through,
          clock: clocks.classifying,
          outcomes: progress.classifying.outcomes,
        },
        {
          label: 'Scoring · base and type',
          state: progress.scoring_base_and_type.state,
          through: progress.scoring_base_and_type.through,
          clock: clocks.scoringBaseAndType,
          outcomes: progress.scoring_base_and_type.outcomes,
        },
        {
          label: 'Scoring · relevance',
          state: progress.scoring_relevance.state,
          through: progress.scoring_relevance.through,
          clock: clocks.scoringRelevance,
          outcomes: progress.scoring_relevance.outcomes,
        },
        {
          label: 'Finalizing',
          state: progress.finalizing.state,
          through: progress.finalizing.through,
          clock: clocks.finalizing,
          outcomes: progress.finalizing.outcomes,
        },
      ]
    : [];

  return (
    <div className="monitor-page">
      <main className="monitor-page__content">
        <div className="monitor-head">
          <div>
            <Link to="/pipeline" className="monitor-head__back">← Pipeline</Link>
            <h1 className="monitor-head__kicker">Scoring run</h1>
            <p>
              {data
                ? `Started ${formatMonitorStamp(data.started_at)} · Finished ${formatMonitorStamp(data.finished_at)} · ${stopped}`
                : loading
                  ? 'Loading…'
                  : error || 'Scoring run not found'}
            </p>
          </div>
          {data && (
            <StatusBadge tone={tone} large>{runStatusLabel(data.status)}</StatusBadge>
          )}
        </div>

        {error && <p className="monitor-error">{error}</p>}

        {data && progress && (
          <>
            <div className="monitor-card monitor-card--pad">
              <h2>Run Snapshot</h2>
              <div className="stg-flow">
                <div className={`stg-card ${cardClass(progress.summarizing.state)}`}>
                  <h3><Dot state={progress.summarizing.state} />Summarizing</h3>
                  <span className="sub">
                    {stageRowStateLabel(progress.summarizing.state)} · {progress.summarizing.through ?? 0} of {of} prompts
                  </span>
                  <OutcomeLines outcomes={progress.summarizing.outcomes} className="sub" />
                  {clocks && <StepTimes clock={clocks.summarizing} />}
                </div>
                <span className="stg-arrow" />
                <div className={`stg-card ${cardClass(progress.classifying.state)}`}>
                  <h3><Dot state={progress.classifying.state} />Classifying</h3>
                  <span className="sub">
                    {stageRowStateLabel(progress.classifying.state)} · {progress.classifying.through ?? 0} of {of} prompts
                  </span>
                  {classifyingWith && <span className="sub">Classified with {classifyingWith}</span>}
                  <OutcomeLines outcomes={progress.classifying.outcomes} className="sub" />
                  {clocks && <StepTimes clock={clocks.classifying} />}
                </div>
                <span className="stg-arrow" />
                <div className={`stg-card ${cardClass(data.stages.scoring)}`} style={{ flex: '1.4 1 260px' }}>
                  <h3><Dot state={data.stages.scoring} />Scoring</h3>
                  <span className="sub">Three parts run at the same time</span>
                  {clocks && (
                    <>
                      <span className="sub">Base and type</span>
                      <StepTimes clock={clocks.scoringBaseAndType} />
                      <span className="sub">Relevance</span>
                      <StepTimes clock={clocks.scoringRelevance} />
                    </>
                  )}
                  <div className={partClass(progress.scoring_base.state)}>
                    <span>Base score</span>
                    {progress.scoring_base.state === 'failed'
                      ? <span>Failed</span>
                      : <Dot state={progress.scoring_base.state} />}
                  </div>
                  <OutcomeLines outcomes={progress.scoring_base.outcomes} className="sub" />
                  <div className={partClass(progress.type_score.state)}>
                    <span>Type score</span>
                    {progress.type_score.state === 'failed'
                      ? <span>Failed</span>
                      : <Dot state={progress.type_score.state} />}
                  </div>
                  <OutcomeLines outcomes={progress.type_score.outcomes} className="sub" />
                  <div className={partClass(progress.scoring_relevance.state)}>
                    <span>Relevance score</span>
                    {progress.scoring_relevance.state === 'failed'
                      ? <span>Failed</span>
                      : <Dot state={progress.scoring_relevance.state} />}
                  </div>
                  <OutcomeLines outcomes={progress.scoring_relevance.outcomes} className="sub" />
                </div>
                <span className="stg-arrow" />
                <div className={`stg-card ${cardClass(progress.finalizing.state)}`}>
                  <h3><Dot state={progress.finalizing.state} />Finalizing</h3>
                  <span className="sub">{stageRowStateLabel(progress.finalizing.state)}</span>
                  <span className="sub">Applies penalties and the overall score</span>
                  <OutcomeLines outcomes={progress.finalizing.outcomes} className="sub" />
                  {clocks && <StepTimes clock={clocks.finalizing} />}
                </div>
              </div>
            </div>

            <div className="monitor-card">
              <div className="monitor-card__head"><h2>Stage by stage</h2></div>
              <div className="monitor-table-wrap">
                <table className="monitor-table" style={{ minWidth: 920 }}>
                  <thead>
                    <tr>
                      <th>Stage</th>
                      <th>Started</th>
                      <th>Finished</th>
                      <th>State</th>
                      <th className="r">Prompts through</th>
                      <th style={{ width: '22%' }}>Progress</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row) => {
                      const pct = progressPct(row.through, of);
                      const rowTone = stageRowTone(row.state);
                      return (
                        <tr key={row.label}>
                          <td>
                            {row.label}
                            {row.extra && <span className="faint">{row.extra}</span>}
                          </td>
                          <td className="nowrap">{formatMonitorStamp(row.clock.startedAt)}</td>
                          <td className="nowrap">{formatMonitorStamp(row.clock.finishedAt)}</td>
                          <td>
                            {row.state === 'pending' ? (
                              <span className="monitor-pill" style={{ padding: '2px 10px', fontSize: 12 }}>
                                {stageRowStateLabel(row.state)}
                              </span>
                            ) : (
                              <StatusBadge tone={rowTone}>{stageRowStateLabel(row.state)}</StatusBadge>
                            )}
                          </td>
                          <td className="r num">
                            <div>{row.through ?? 0} / {of}</div>
                            <OutcomeLines outcomes={row.outcomes} className="faint" />
                          </td>
                          <td>
                            <div className="progress-bar">
                              <i
                                className={row.state === 'failed' ? 'failed' : ''}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="monitor-note">This page shows stages only. It does not list or open any person&apos;s prompts.</p>
          </>
        )}
      </main>
    </div>
  );
};

export default ScoringRun;
